import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import { recordWatch } from '@/store/slices/historySlice';
import type { RecordWatchInput, WatchEntry } from '@/services/historyApi';
import type { SourceOfTruth, HistoryStatus } from '@/store/slices/historySlice';

export interface UseWatchHistory {
  entries: WatchEntry[];
  sourceOfTruth: SourceOfTruth;
  status: HistoryStatus;
  error: string | null;
  merging: boolean;
  recordWatch: (input: RecordWatchInput) => void;
}

/**
 * Read access to the watch-history slice plus the canonical `recordWatch` action.
 *
 * Behavior depends on `sourceOfTruth`:
 * - `local`:  the upsert happens in the slice reducer and is mirrored to
 *             localStorage. Used for anonymous and guest sessions.
 * - `server`: the upsert happens server-side via POST /api/history, and the
 *             returned entry replaces the matching local one.
 *
 * The transition from local → server is driven by the merge effect in
 * `App.tsx`, not by callers of this hook.
 */
export function useWatchHistory(): UseWatchHistory {
  const dispatch = useAppDispatch();
  const { entries, sourceOfTruth, status, error, merging } = useAppSelector((s) => s.history);

  const record = useCallback(
    (input: RecordWatchInput) => {
      void dispatch(recordWatch(input));
    },
    [dispatch],
  );

  return { entries, sourceOfTruth, status, error, merging, recordWatch: record };
}
